<template>
    <div
        ref="gridElement"
        class="h-100 overflow-auto d-flex flex-column gap-3"
    >
        <ag-grid-vue
            class="eq-ag-theme eq-theme-compact h-100"
            :class="[currentTheme == 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz', listMode ? 'ag-grid-list grid-borderless' : '']"
            :onFirstDataRendered="onFirstDataRendered"
            :onSelectionChanged="onSelectionChanged"
            :onCellMouseOver="onCellMouseOver"
            :onCellMouseOut="onCellMouseOut"
            :columnDefs="columnDefs"
            :statusBar="listMode ? undefined : statusBar"
            :cellSelection="true"
            theme="legacy"
            :selectionColumnDef="gridSelectionColumnDef"
            :gridOptions="gridOptions"
            :animateRows="false"
            :suppressServerSideFullWidthLoadingRow="true"
            :isServerSideGroupOpenByDefault="isGroupOpenByDefault"
            :blockLoadDebounceMillis="0"
            :detailCellRenderer="AgDetailCellrenderer"
            :masterDetail="isMasterDetailGrid()"
            :detailCellRendererParams="detailCellRendererParams"
            :detailRowAutoHeight="true"
            groupDisplayType="groupRows"
            :rowClassRules="agRowClassRules"
            :onFillEnd="onFillEnd"
            :onGridReady="onGridReady"
            :onCellValueChanged="onCellValueChanged"
            :getRowId="getRowId"
            :autoGroupColumnDef="autoGroupColumnDef"
            :stopEditingWhenCellsLoseFocus="true"
            @cell-editing-started="onCellEditingStarted"
            @cell-editing-stopped="onCellEditingStopped"
            @cell-focused="onCellFocused"
            @cell-clicked="onCellClicked"
            @row-selected="onRowSelected"
            @cell-key-down="onCellKeyDown"
            @sort-changed="onSortChanged"
            @cell-double-clicked="onCellDoubleClicked"
            @state-updated="onStateUpdated"
        />
    </div>
</template>

<script lang="ts" generic="TRowData extends object, TParentRowData = null" setup>
import { AgGridVue } from 'ag-grid-vue3';
import {
    type CellClassParams,
    type CellClickedEvent,
    type CellDoubleClickedEvent,
    type CellEditingStartedEvent,
    type CellEditingStoppedEvent,
    type CellFocusedEvent,
    type CellKeyDownEvent,
    type CellMouseOutEvent,
    type CellMouseOverEvent,
    type CellValueChangedEvent,
    type ColDef,
    type ColDefField,
    type Column,
    type ColumnSizeState,
    type EditableCallbackParams,
    type FillEndEvent,
    type FirstDataRenderedEvent,
    type GetRowIdParams,
    type GridApi,
    type GridReadyEvent,
    type IRowNode,
    type IServerSideDatasource,
    type IServerSideGetRowsParams,
    type IsServerSideGroupOpenByDefaultParams,
    type ProcessCellForExportParams,
    type ProcessDataFromClipboardParams,
    type RowClassParams,
    type SelectionChangedEvent,
    type SortChangedEvent,
    type StateUpdatedEvent,
    type StatusPanelDef,
    type ValueGetterParams,
    type ValueSetterParams,
    CellStyleModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    CustomEditorModule,
    CustomFilterModule,
    DateFilterModule,
    ModuleRegistry,
    NumberFilterModule,
    PinnedRowModule,
    RenderApiModule,
    RowDragModule,
    RowStyleModule,
    ScrollApiModule,
    ValidationModule,
    GridStateModule,
} from 'ag-grid-community';
import {
    ServerSideRowModelModule,
    MasterDetailModule,
    RowGroupingModule,
    StatusBarModule,
    ClipboardModule,
    ServerSideRowModelApiModule,
    SetFilterModule,
    MultiFilterModule,
    GroupFilterModule,
    CellSelectionModule,
    ExcelExportModule,
} from 'ag-grid-enterprise';
import AgCellEditorWrapper from './AgWrappers/AgCellEditorWrapper.vue';
import AgCellRendererWrapper from './AgWrappers/AgCellRendererWrapper.vue';
import AgHeaderComponentWrapper from './AgWrappers/AgHeaderComponentWrapper.vue';
import {
    GridColumnOptions,
    GridSelectionState,
    GridSetup,
    type IGridColumnCssRule,
    type IGridColumnSort,
    type IGridCompExposed,
} from './Classes';
import { StandardHeader } from './';
import { useThemeStoreRef } from '@/stores';
import { $tm } from '@/locize';
import { watch, computed, ref, onMounted, onUnmounted, type Component, markRaw } from 'vue';
import { useElementVisibility } from '@vueuse/core';
import { GridStateColumn } from './Classes/GridStateColumn';
import { CustomGridState } from './Classes/CustomGridState';
import { useGridSettings } from '@/composeables/grid/gridSettings';
import { useDebouncer } from '@/utilities/Debouncer';
import { ClipboardDataItem } from '@/composeables/clipboard/clipboardData';
import { useClipboardHandler } from '@/composeables/clipboard/clipboardhandler';
import { guessValueType, propertyof, ThreadSleep } from '@/utilities/utils';
import { type ISourceValue } from './Classes/ISourceValue';
import type { IGridColumnFilter } from './Classes/IGridColumnFilter';
import type { IGridFilterValue } from './Classes/IGridFilterValue';
import RowcountPanel from './Statusbar/RowcountPanel.vue';
import AggregateCellRenderer from './CellRenderers/AggregateCellRenderer.vue';
import type { IGridAggregate } from './Classes/IGridAggregate';
import EmptyCellRenderer from './CellRenderers/EmptyCellRenderer.vue';
import GroupRowRenderer from './CellRenderers/GroupRowRenderer.vue';
import AgDetailCellrenderer from './CellRenderers/AgDetailCellrenderer.vue';
import type { GridResizeStrategy } from './Classes/GridResizeStrategy';
import type { IGridDetailExposed } from './Classes/IGridDetailExposed';
import type { RowNodeEvent } from 'node_modules/ag-grid-community/dist/types/src/interfaces/iRowNode';
import type { SelectionColumnDef } from 'ag-grid-enterprise';
import type { DragStoppedEvent, GridOptions, ICellEditorParams, ValueParserParams } from 'ag-grid-community';
import type { ICellRendererParams } from 'ag-grid-community';
import type GridCheckboxSelectionColumnOptions from './Classes/GridCheckboxSelectionColumnOptions';
import { agSortToSortDirection, sortDirectionToAgSort } from '@/utilities';
import type { SortDirection } from '@/objects/generated';
import { ConvertCellRendererValueToString, ConvertClipboardDataToCellEditorValue } from '@/objects/Valueparsers/ComponentValueUtils';
import { TimeSpan } from '@/objects/TimeSpan';
import { compareDateTimesNullable, compareTimespansNullable } from '@/utilities/dateutils';

ModuleRegistry.registerModules([
    CellSelectionModule,
    CellStyleModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    ClipboardModule,
    CustomEditorModule,
    MasterDetailModule,
    PinnedRowModule,
    RenderApiModule,
    RowDragModule,
    RowGroupingModule,
    RowStyleModule,

    ScrollApiModule,
    ServerSideRowModelApiModule,
    ServerSideRowModelModule,
    StatusBarModule,
    ValidationModule,

    CustomFilterModule,

    ExcelExportModule,

    GridStateModule,

    // Do we need these ones? I think we do all by customfilter
    DateFilterModule,
    GroupFilterModule,
    MultiFilterModule,
    NumberFilterModule,
    SetFilterModule,
]);

const gridElement = ref<HTMLElement | null>(null);

/**
 * A handle to the element wrapping the grid
 */
const isGridVisible = useElementVisibility(gridElement);

/**
 * The current theme of the application
 */
const { currentTheme } = useThemeStoreRef();

/**
 * The props for the grid
 */
const props = withDefaults(
    defineProps<{
        gridSetup: GridSetup<TRowData>;

        /**
         * If we should render the grid as listmode.
         * This will render the grid as a list and not a table
         * It will remove some styling and make the grid look more like a list
         * @default false
         */
        listMode?: boolean;

        /**
         * The resize strategy for the grid
         * @default 'fitToContent' The grid will try and resize to fit the content
         */
        resizeStrategy?: GridResizeStrategy;

        /**
         * If true, the grid wont resize on load / inital visibility
         * The only way to make the grid resize itself if this is true is to call the {@link resizeColumns} method
         */
        disableGridAutoResizeColumnsOnLoad?: boolean;

        /**
         * The columns we want to autoresize when the grid runs the {@link resizeColumns} method
         *
         * If this is not supplied, all columns are resized
         *
         * NOTE: This is only works if the {@link resizeStrategy} is set to 'fitToContent'
         */
        resizeColumnIds?: string[];

        /**
         * The absolute height of the grid
         */
        height?: number;

        /**
         * The interval value we use for date columns when we fill down
         * Default is 30000 milliseconds (ms) or 30 seconds
         * @default 30000
         */
        dateinterval?: number;

        /**
         * The interval value we use for number columns when we fill down
         * @default 1
         */
        numberinterval?: number;

        /**
         * If we should keep the columns outside the viewport
         * NOTE! This will always keep all columns so we need to check how this affects performance. The advantage of doing this is that we dont have to keep track of states for the columns ourself.
         * The state is the filter selection and sortorder of each column.
         * @default true (This is the not the default behavior of ag-grid)
         */
        keepColumnsOutsideViewport?: boolean;

        /**
         * If this is a detail grid, this contains the parent row
         * TODO: We need to know the type
         * @default null
         */
        parentrow?: any;

        /**
         * The view we want to show when we click on a master row
         */
        gridDetailView?: Component;

        /**
         * The title of the detail view.
         * @default undefined
         */
        gridDetailTitle?: string;
    }>(),
    {
        dateinterval: 30000,
        listMode: false,
        numberinterval: 1,
        keepColumnsOutsideViewport: true,
        resizeStrategy: 'fitToContent',
        disableGridAutoResizeColumnsOnLoad: false,
        parentrow: null,
    },
);

/**
 * Will force the grid to resize the columns when the grid is visible
 */
watch(
    isGridVisible,
    (visible) => {
        if (props.disableGridAutoResizeColumnsOnLoad) {
            return;
        }

        if (visible) {
            resizeColumns();
        }
    },
    { immediate: true },
);

// A list of all the current active fields in the grid
// The key is the columnId and the value is the filters
const columnFilters = new Map<string, IGridColumnFilter[]>();

// Helper function to deffer/debounce async calls
const statesaveDebounce = useDebouncer();

// the debouncer for resizing the columns
const resizeDebounce = useDebouncer();

/** GridAPi so we can interact with the ag-grid */
let gridApi: GridApi;

const gridDetailGrids = ref<IGridDetailExposed<TParentRowData>[]>([]);

const registerGrid = (detailgrid: IGridDetailExposed<any>) => {
    // @ts-ignore : This is a bug in the type definition for some reason the value is unwrapped
    gridDetailGrids.value.push(detailgrid);
};

/**
 * Dirty cells that have been edited
 */
let dirtyCells: Record<string, any> = {};

// Define the status panels for the grid. This is the statusbar at the bottom of the grid
const statusBar = ref<{ statusPanels: StatusPanelDef[] } | undefined>(undefined);

// If we have a total number of rows we add a rowcount panel to the statusbar
if (!props.gridSetup.hideStatusbar) {
    let panels: StatusPanelDef[] = [];
    if (props.gridSetup.datasource.rowCountGetter) {
        panels.push({
            statusPanel: RowcountPanel,
            statusPanelParams: {
                rowCount: props.gridSetup.datasource.rowCountGetter,
                totalCount: props.gridSetup.datasource.totalNumberOfRows,
            },
        });
    }
    statusBar.value = panels.length == 0 ? undefined : { statusPanels: panels };
}

const emit = defineEmits<{
    (e: 'onSelectionChanged', selectedRows: TRowData[]): void;
    (e: 'onCellMouseOver', row: TRowData): void;
    (e: 'onCellMouseOut', row: TRowData): void;
    (e: 'rowHovered', row: TRowData | undefined): void; // TODO: Do we want undefined or null here?
    (e: 'cellValueChanged', row: TRowData, columnId: string, newVal: any, oldVal: any): void; // TODO: Can we not have any as the type of the new value here? We need to know what type of data in row[columnId] is.
    (e: 'onCellFocused', rowId: string, columnId: string): void;
    (e: 'onCellClicked', rowId: string, columnId: string): void;
    (e: 'onRowSelected', rowId: string): void;
    (e: 'gridReady', state: CustomGridState | null): void;
    (e: 'onCellEditingStopped', row: TRowData, columnId: string, rowId?: string): void;
    (e: 'onCellEditingStarted', row: TRowData, columnId: string, rowId?: string): void;
    (e: 'onIdColumnClicked', id: number): void;
    (e: 'onFirstDataRendered'): void;
    (e: 'onCellKeyDown', event: KeyboardEvent): void;
    (e: 'sortChanged'): void; // TODO: Should we include the new sort model here?
    (e: 'onCellDoubleClicked', row: TRowData, cellData: any, rowId: string, columnId: string): void;

    /**
     * Whenever the stored state is updated
     */
    (e: 'onStateUpdated', state: CustomGridState | null): void;
}>();

let pinnedTopRowDataIds: string[] = [];
let pinnedBottomRowDataIds: string[] = [];

let refreshPromise: Promise<void> | null = null;
let refreshPromiseResolver: (() => void) | null = null;

let purgePromise: Promise<void> | null = null;
let isPurging = false;

/**
 * This will refresh all the aggregates, statusbar and the values in the grid.
 * @remarks This will not purge the cache
 */
const refreshCachedData = async () => {
    // If we are already refreshing, just the caller can wait until we are done refreshing
    if (refreshPromise) {
        return refreshPromise;
    }

    // If we are purging, we can wait for that instead. Its basically the same
    if (purgePromise) {
        return purgePromise;
    }

    // Extra isPurging lock to avoid raceCondition
    if (isPurging) {
        await ThreadSleep(0);
        if (purgePromise) {
            console.warn('We got into the isPurging if'); // I'm not sure if this will ever happen so I want a visible notice in the console if it does
            return purgePromise;
        }
    }

    dirtyCells = {};

    refreshAggregates(); // We rerun the aggregate functions

    refreshStatusbar(); // We rerun the statusbar functions

    // Promise so we have something to wait on from the callsite
    refreshPromise = new Promise<void>((resolve) => {
        // When we resolve, we reset the promise and the resolver
        refreshPromiseResolver = () => {
            resolve();
            refreshPromiseResolver = null;
            refreshPromise = null;
        };
    });

    gridApi?.refreshServerSide({ purge: false });

    return refreshPromise;
};

/**
 * This will first purge the cache and then refresh all the aggregates, statusbar and the values in the grid.
 * @remarks This will basically clear all data and redraw all rows from scratch
 */
const purgeCachedData = async () => {
    dirtyCells = {};

    refreshAggregates(); // We rerun the aggregate functions

    refreshStatusbar(); // We rerun the statusbar functions

    isPurging = true;

    purgePromise = new Promise<void>(async (resolve) => {
        purgePromise = null;
        if (refreshPromise) {
            await refreshPromise;
        }
        refreshPromiseResolver = () => {
            resolve();
            refreshPromiseResolver = null;
            purgePromise = null;
            isPurging = false;
        };
    });

    if (refreshPromise) {
        await refreshPromise;
    }

    // Call the purge, this will eventually end up calling the refreshPromiseResolver we set inside the purgepromise
    gridApi?.refreshServerSide({ purge: true });

    return await purgePromise;
};

/**
 * Select all the rows in the grid
 * @param select If we should select or deselect the rows
 * @default true
 * @remarks This will only select the rows that are currently loaded in the grid
 */
const selectAllRows = (select: boolean = true) => {
    // Foreaach all rownodes
    gridApi?.forEachNode((node: IRowNode<TRowData>) => {
        node.setSelected(select, true);
    });

    // We need to update the selection state in the datasource
    rowSelectionState.selectAll = select;
    gridApi?.setServerSideSelectionState(rowSelectionState.toAGGridState());
};

/**
 * Sets the sort state of the grid
 * This will override any existing sort state
 * @param newSortState The new sorting state
 */
const setSortState = (newSortState: IGridColumnSort[]) => {
    gridApi.applyColumnState({
        state: newSortState.map((s, i) => ({
            colId: s.columnId,
            sort: sortDirectionToAgSort(s.sortDirection),
            sortIndex: i,
        })),
        defaultState: { sort: null },
    });
};

/** Exposed functions for the grid */
const exposed = {
    refreshHeader: () => gridApi.refreshHeader(),

    /**
     * This will refresh all the aggregates, statusbar and the values in the grid.
     * @remarks This will not purge the cache
     */
    refreshCachedData: refreshCachedData,

    /**
     * This will first purge the cache and then refresh all the aggregates, statusbar and the values in the grid.
     * @remarks This will basically clear all data and redraw all rows from scratch
     */
    purgeCachedData: purgeCachedData,

    /**
     * Get the selected rows in the grid
     */
    getSelectedRowState: () => getSelectedRowState(),

    /**
     * Get the selected rows in the grid
     * @param data
     */
    getSelectedRows: () => getSelectedRows(),

    /**
     * Select all the rows in the grid
     * @remarks This will only select the rows that are currently loaded in the grid
     */
    selectAllRows: () => selectAllRows(true),

    /**
     * Deselect all the rows in the grid
     * @remarks This will only select the rows that are currently loaded in the grid
     */
    deselectAllRows: () => selectAllRows(false),
    selectRowsByRowId: (rowIds: string[]) => {
        gridApi.forEachNode((node: IRowNode<TRowData>) => {
            // God only knows how a node cannot have an id, but its apparently possible
            if (!node.id) {
                return;
            }
            node.setSelected(rowIds.includes(node.id));
        });
    },

    /**
     * Set the pinned rows by rowId
     * @param rowIds
     * @param position
     */
    pinRows(rowIds: string[], position: 'top' | 'bottom') {
        const rowNodes: IRowNode<any>[] = [];
        for (const id of rowIds) {
            const rowNode = gridApi.getRowNode(id);
            if (rowNode) {
                rowNodes.push(rowNode.data);
            }
        }

        if (position === 'top') {
            gridApi.setGridOption('pinnedTopRowData', rowNodes);
            pinnedTopRowDataIds = pinnedTopRowDataIds.concat(rowIds);
        } else {
            gridApi.setGridOption('pinnedBottomRowData', rowNodes);
            pinnedBottomRowDataIds = pinnedBottomRowDataIds.concat(rowIds);
        }
    },

    /**
     * Unpin rows by rowId
     * @param rowIds
     * @param position
     */
    unpinRows(position: 'top' | 'bottom') {
        if (position === 'top') {
            gridApi.setGridOption('pinnedTopRowData', []);
        } else {
            gridApi.setGridOption('pinnedBottomRowData', []);
        }
    },

    /**
     * refresh the cells in the grid for the given rowIds and columns
     * @remarks pass undefined to rowIds to refresh all rows
     * @param position
     */
    refreshCells: (rowIds?: string[], columns?: string[]) => {
        if (!gridApi) {
            return;
        }

        const rowNodes: IRowNode<any>[] = [];

        if (rowIds && rowIds?.length > 0) {
            rowIds.forEach((id) => {
                const rowNode = gridApi.getRowNode(id);
                if (rowNode) {
                    rowNodes.push(rowNode);
                }
            });
        }

        gridApi.refreshCells({
            force: true,
            columns: columns,
            rowNodes: rowNodes.length > 0 ? rowNodes : undefined,
        });
    },

    /**
     * Resize the columns in the grid
     * NOTE: This might need to be debounced.
     */
    resizeColumns: (force?: boolean) => resizeColumns(force),

    /**
     * Force the grid to stop editing
     * @param cancel
     */
    stopEditing: (cancel?: boolean) => {
        gridApi.stopEditing(cancel);
    },
    startEditing: (rowId: string, columnId: string) => {
        const idx = gridApi.getRowNode(rowId)?.rowIndex;
        if (idx === undefined || idx === null) {
            console.warn(`Row not found with id: ${rowId}`);
            return;
        }
        const current = gridApi.getEditingCells();
        if (current.length == 0 || !(current[0].rowIndex == idx && current[0].colId == columnId)) {
            gridApi.startEditingCell({ rowIndex: idx, colKey: columnId });
        } else {
            console.warn('Already editing cell');
        }
    },

    /**
     * This will focus the cell in the grid on a given index no index will focus the first cell in the grid.
     * An index of negative number will focus the first cell of the last row in the grid
     */
    focus: (row: 'first' | 'last' | 'added') => {
        let nodeIndex: number | null =
            row == 'first' ? gridApi.getFirstDisplayedRowIndex() : row == 'last' ? gridApi.getLastDisplayedRowIndex() : null;

        // This will focus on the first added row in the grid. We assume that added rows have ID <= 0
        // This will also take in account order and filtering.
        // TODO: AddInGrid actually has a potential bug since if we have a filter i.e we are filtering on gender = FEMALE, and we add a new row with default Category
        // They will get a default gender of MALE and therefor will not be in the GUI and we cant focus on them.
        if (nodeIndex == null && row == 'added') {
            gridApi.forEachNode((node: IRowNode<TRowData>) => {
                if (nodeIndex == null && node.id && parseInt(node.id) <= 0) {
                    nodeIndex = node.rowIndex;
                }
            });
        }

        // We couldnt find a index to focus on
        if (nodeIndex == null) {
            return;
        }

        // We try to find the node at the index
        let focusnode: IRowNode<TRowData> | undefined = gridApi.getDisplayedRowAtIndex(nodeIndex);

        // If we still couldnt find a node we return
        if (!focusnode) {
            return;
        }

        // Make sure we have any displayed columns so we can focus on the first one
        let displayedColumns = gridApi.getAllDisplayedColumns();
        if (displayedColumns == null || displayedColumns.length == 0) {
            return;
        }

        gridApi.setFocusedCell(focusnode?.rowIndex!, displayedColumns[0]);

        emit('onCellFocused', focusnode.id!, displayedColumns[0].getId());

        if (row == 'added') {
            // A bit hacky way to move the scroll a bit below the added line
            // The alternative is to keep a global ref to the pagecontent element, but I dont think that is worth it
            const contentElement = document.getElementById('pagecontent');
            if (contentElement) {
                contentElement.scrollTop = contentElement.scrollTop + 100;
            }
        }
    },

    /**
     * Get the unique values of a cell in the grid
     * TODO: This will return the value and not the display value, we might want to change this so we can get both. Since we most likely
     * want to show the display value in the UI. For now this is ok since for the columns we want to show are only text coloumns or numbers then
     * we can use this to get the unique values.
     *
     * We can get first get the unique values and then we run the cellrenderer on the values to get the display value. We should also try and find the actual datatype of the column and return that one too.
     * @param rowId
     * @param columnId
     */
    getUniqueFilterColumnValues: (columnId: string, includeRenderer: boolean = true): IGridFilterValue[] => {
        // Get the unique values of a column
        let columnOptions = props.gridSetup.GridColumnDefinition.ColumnOptions.find((c) => c.columnId == columnId);

        // Check if we have pinned rows and find the rowid of the pinned rows
        let arrayValues: Map<string, any> = new Map<string, any>();
        if (columnOptions && typeof columnOptions.filterValues == 'function') {
            arrayValues = columnOptions.filterValues!(pinnedTopRowDataIds.concat(pinnedBottomRowDataIds));
        }

        // We convert the map to an array of IGridFilterValue
        const filterValues: IGridFilterValue[] = [];

        // We guesstimate the datatype of the column based on the first value
        let textDataType: string = 'string';

        if (arrayValues.size > 0) {
            let value = arrayValues.values().next().value;
            let text = arrayValues.keys().next().value ?? '';

            // We try and see if it is a number
            if (!isNaN(parseInt(text))) {
                textDataType = 'number';
            } else if (guessValueType(value).guess == 'Date' && !isNaN(new Date(text)?.getFullYear())) {
                textDataType = 'date';
            }
        }

        // If we have dates, we need to convert the value to a number and get the unique values
        if (textDataType == 'date') {
            arrayValues = new Map<string, any>(
                Array.from(arrayValues.entries()).map(([key, value]) => {
                    let dateValue = new Date(key);
                    if (!isNaN(dateValue.getTime())) {
                        return [dateValue.getTime().toString(), value];
                    }
                    return [key, value];
                }),
            );
        }

        for (const [key, value] of arrayValues.entries()) {
            filterValues.push({
                value: value,
                text: textDataType == 'date' ? ((value as Date)?.toISOString() ?? key) : key,
                renderer: includeRenderer ? columnOptions?.cellRenderer : undefined,
                rendererParams: includeRenderer ? columnOptions?.cellRendererParams : undefined,
            });
        }

        return filterValues;
    },

    /**
     * Set the filter for the grid
     * @param filter
     */
    setFilter: (filtersToApply: IGridColumnFilter[]) => {
        filtersToApply.forEach((filter) => {
            const filterType = filter.searchType;

            // Filters now is the old filters with the current searchType replaced
            let _filters = [...(columnFilters.get(filter.columnId)?.filter((f) => f.searchType != filterType) ?? []), filter];

            // Empty filters are deleted, otherwise we update the filter in the map
            if (!_filters || filtersToApply.length == 0 || filter.values.length == 0) {
                columnFilters.delete(filter.columnId);
            } else {
                columnFilters.set(filter.columnId, _filters);
            }
        });

        purgeCachedData();
    },

    /**
     * We remove all the filters for a column
     * @param columnId
     */
    removeFilter: (columnId: string) => {
        // We need to force the grid to refresh the data
        columnFilters.delete(columnId);
    },

    /**
     * This will remove a column from the grid
     * @param columnId
     */
    removeColumns: (columnIds: string[]) => {
        // Get all the fieldIds in the current layout
        let coldefs = gridApi.getColumnDefs()?.map((c) => c as ColDef) ?? [];

        // Remove the columnId from the fieldIds
        coldefs = coldefs.filter((f) => !columnIds.includes(f.field ?? ''));

        // Apply the new column layout
        gridApi.setGridOption('columnDefs', coldefs);
    },

    /**
     * Force the grid to move to a specific rowId
     * @remarks This will only work if the row is allready loaded and rendered in the grid.
     */
    ensureRowVisible: (rowId: string) => {
        const node = gridApi.getRowNode(rowId);
        if (node) {
            gridApi.ensureNodeVisible(node);
        }
    },

    /**
     * Trigger the flashCells event on the grid for the given rowIds and columnIds
     * This will flash the cell for about a 1 second
     * @param rowIds
     * @param columnIds
     * @returns
     */

    flashCells: (rowIds: string[], columnIds: string[]) => {
        const rownodes = (rowIds.map((id) => gridApi.getRowNode(id)) ?? []).filter((r) => r != null) ?? [];

        if (rownodes.length == 0) {
            return;
        }
        gridApi.flashCells({ rowNodes: rownodes, columns: columnIds });
    },

    /**
     * This will trigger the save method of all the detail grids
     * This is a completely async operation and will not provide us with any feedback
     * @returns
     */
    saveDetailGrids: async () => {
        const promises: Promise<void>[] = [];
        gridDetailGrids.value.forEach((grid) => {
            promises.push(grid.SaveGrid());
        });

        return Promise.all(promises);
    },

    /**
     * Expand the detail grid for the given row id
     * @param rowId  The id of the master grid row
     * @returns
     */
    expandDetailGrid: (rowId: string) => {
        gridApi.getRowNode(rowId)?.setExpanded(true);
    },

    /**
     * Collapse the detail grid for the given row id
     * @param rowId  The row id of the master grid row
     * @returns
     */
    collapseDetailGrid: (rowId: string) => {
        gridApi.getRowNode(rowId)?.setExpanded(false);
    },

    /**
     * Sets the sort state of the grid
     * This will override any existing sort state
     * @param newSortState The new sorting state
     */
    setSortState: setSortState,

    /**
     * Get the latest sortstate of the grid as it was in the latest getRows call
     */
    getSortState: (): IGridColumnSort[] => lastColumnSort,

    /**
     * Export as excel
     */
    exportToExcel: async (fileName: string, sheetName: string) => {
        await loadAllData();

        return gridApi.exportDataAsExcel({
            processCellCallback: (params) => {
                const coldef = props.gridSetup.GridColumnDefinition.ColumnOptions.find((c) => c.columnId === params.column.getColId());

                if (coldef && coldef.valueFormatterForExport) {
                    return coldef.valueFormatterForExport(params.node?.data);
                }
                // For other columns, return the value as is
                return params.value;
            },
            fileName: fileName,
            sheetName: sheetName,
        });
    },

    /**
     * Hides the given columns in the grid
     * @param columnIds The column ids to hide
     */
    hideColumns: (columnIds: string[]) => setColumnVisibility(columnIds, false),

    /**
     * Shows the given columns in the grid
     * @param columnIds The column ids to show
     */
    showColumns: (columnIds: string[]) => setColumnVisibility(columnIds, true),

    /**
     * Return the current gridstate
     */
    getGridstate: () => getCurrentGridState(),

    /**
     * Reset the gridstate to the default state as defined in the props
     */
    resetGridState: () => resetGridState(),
};

/**
 * Maps the columnId to dataField so we can find one from the other in the cases where they are not the same
 * This is a complete set for all the columns in the grid
 */
const gridColumnIdToDataFieldMap = computed(
    () => new Map<string, string>(props.gridSetup.GridColumnDefinition.ColumnOptions.map((c) => [c.columnId, c.dataField])),
);

/**
 * Callback function for setting the state of the groped rows
 * @param params
 */
const isGroupOpenByDefault = (params: IsServerSideGroupOpenByDefaultParams) => {
    return false;
};

/**
 * Map over the columnId to valueGetter function.
 * Missing valuegetter functions are generated so this is a complete set.
 */
const gridColumnValueGetterMap = computed(
    () =>
        new Map<string, (data: TRowData) => any>(
            props.gridSetup.GridColumnDefinition.ColumnOptions.map((c) => [
                c.columnId,
                c.valueGetter ??
                    ((row: TRowData) => {
                        // Find the dataField for the columnId
                        const lookup = gridColumnIdToDataFieldMap.value.get(c.columnId)!;
                        // If the dataField has a dot its in an array member
                        if (lookup.includes('.')) {
                            const lookupParts = lookup.split('.');
                            let val = row;
                            // Loop incase its nested
                            for (const part of lookupParts) {
                                val = (val as any)[part];
                            }
                            // This should be the final value
                            return val;
                        }
                        // If its not nested we can just return the value
                        return (row as any)[lookup];
                    }),
            ]),
        ),
);

/**
 * Map over the columnId to sortValueGetter function.
 * This will only have the columns that have the sortValueGetter defined.
 */
const gridColumnSortValueGetterMap = computed(
    () =>
        new Map<string, (data: TRowData, sortDirection: SortDirection) => any>(
            props.gridSetup.GridColumnDefinition.ColumnOptions.filter((c) => c.sortValueGetter !== undefined).map((c) => [
                c.columnId,
                c.sortValueGetter!, // We must have a sortValueGetter here since we filter on columns that have sortValueGetters
            ]),
        ),
);

/**
 * This will update the visibility and rerender the grid based on columnids
 */
const setColumnVisibility = (columnIds: string[], visible: boolean) => {
    const colState = columnIds.map((colId) => ({
        colId: colId,
        hide: !visible,
    }));

    gridApi?.applyColumnState({
        state: colState,
    });
};

/**
 * This is the filter function that is called when we need to filter the data
 * @param allrows
 * @param filters
 */
const getFilteredRows = (allrows: TRowData[], filters: IGridColumnFilter[]): TRowData[] => {
    let filteredRows = allrows;
    for (const filter of filters) {
        filteredRows = filteredRows.filter((row) => {
            let value = getGridCellValue(row, filter.columnId);
            if (value === undefined) {
                return false;
            }

            let filterValues = filter.values;
            if (filterValues === undefined) {
                return true;
            }

            // If we have a value getter we use it to get the value of the value of the cell
            // This is used in cases where we have a value that is not directly in the row data but is a complex object
            if (filter.valuegetter && typeof filter.valuegetter == 'function') {
                value = filter.valuegetter(value);
                filterValues = filterValues.map((v) => filter.valuegetter!(v));
            } else {
                if (filterValues.length > 0 && filterValues[0] instanceof Date) {
                    // If we are filtering on a date column we convert the value to a timestamp so we can compare it with the filter values
                    value = new Date(value).getTime();
                    filterValues = filterValues.map((v) => new Date(v).getTime());
                }
            }

            switch (filter.searchType) {
                case 'equals':
                    if (filterValues.includes(value)) {
                        return true;
                    }
                    break;
                case 'contains':
                    if (filterValues.some((v) => value.toString().includes(v))) {
                        return true;
                    }
                    break;
                case 'startsWith':
                    if (filterValues.some((v) => value.toString().startsWith(v))) {
                        return true;
                    }
                    break;
                case 'endsWith':
                    if (filterValues.some((v) => value.toString().endsWith(v))) {
                        return true;
                    }
                    break;
                default:
                    return false;
            }
        });
    }

    if (lastFilteredRows != null && rowSelectionState.toggledNodes.length > 0) {
        // rowSelectionState.rowIds = filteredRows.map((r) => getRowIdForRow(r));
        let filteredIds = filteredRows.map((r) => getRowIdForRow(r));
        rowSelectionState.toggledNodes = rowSelectionState.toggledNodes.filter((x) => filteredIds.includes(x));

        // Save the new state
        gridApi.setServerSideSelectionState(rowSelectionState.toAGGridState());
    }

    return filteredRows;
};

const defaultSortFunction = (allrows: TRowData[], columnSort: IGridColumnSort[]) => {
    // Pre-compute all sort values once per sort pass (O(N)) rather than inside the comparator (O(N log N))
    const sortValueCache = new WeakMap<TRowData & object, Map<string, any>>();
    for (const row of allrows) {
        const colValues = new Map<string, any>();
        for (const sort of columnSort) {
            colValues.set(sort.columnId, getGridCellSortValue(row, sort.columnId, sort.sortDirection));
        }
        sortValueCache.set(row, colValues);
    }

    allrows.sort((a, b) => {
        for (const sort of columnSort) {
            const aVal = sortValueCache.get(a)!.get(sort.columnId);
            const bVal = sortValueCache.get(b)!.get(sort.columnId);

            // If aVal and bVal are objects there is no default way to compare them
            // We only need to check aVal since bVal will be the same type
            // instanceof Object will return true for arrays and functions as well so we are covered
            if (aVal instanceof Date || bVal instanceof Date) {
                // We dont do anything here since we want to compare the dates
            } else if (aVal instanceof TimeSpan || bVal instanceof TimeSpan) {
                // We dont do anything here since we want to compare the timespans
            } else if (aVal instanceof Object || bVal instanceof Object) {
                // We explicitly return 0 here since we dont want to change the order of the rows if they cant be compared
                continue;
            }

            if (aVal === bVal) {
                continue;
            }

            let sd = sort.sortDirection == 'Ascending' ? 1 : -1;

            if (typeof aVal === 'number' || typeof bVal === 'number') {
                return ((aVal ?? 0) > (bVal ?? 0) ? 1 : -1) * sd;
            } else if (typeof aVal === 'string' || typeof bVal === 'string') {
                return (aVal ?? '').localeCompare(bVal ?? '', 'nb') * sd;
            } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
                return (aVal === bVal ? 0 : aVal ? 1 : -1) * sd;
            } else if (aVal instanceof TimeSpan || bVal instanceof TimeSpan) {
                return compareTimespansNullable(aVal, bVal) * sd;
            } else if (aVal instanceof Date || bVal instanceof Date) {
                return compareDateTimesNullable(aVal, bVal) * sd;
            } else {
                return 0;
            }
        }
        return 0;
    });
};

/**
 * Gets the value of a member in the row data, based on the columnId
 */
const getGridCellValue = (row: TRowData, columnId: string) => gridColumnValueGetterMap.value.get(columnId)!(row);

/**
 * Gets the current sort value for a grid cell
 * This will use the sortValueGetter if it exists, otherwise it will fallback to the valueGetter for the column
 * @param row The row we want to get a cell value for
 * @param columnId The column in the row we want to get the sort value for
 */
const getGridCellSortValue = (row: TRowData, columnId: string, sortDirection: SortDirection) => {
    if (gridColumnSortValueGetterMap.value.has(columnId)) {
        return gridColumnSortValueGetterMap.value.get(columnId)!(row, sortDirection);
    }
    // If we have a sortValueGetter we use that, otherwise we use the valueGetter
    return gridColumnValueGetterMap.value.get(columnId)!(row);
};

/**
 * Contains the current expanded groups
 * TODO: This should also be a part of the state we save so we remember the expanded groups
 */
const expandedGroupState = new Set<string>();

/**
 * Previous successcallback rows
 */
let lastFilteredRows: TRowData[] | null = null;

/**
 * Previous column sort from when the grid triggered a getRows call
 */
let lastColumnSort: IGridColumnSort[] = [];

/**
 * Flag to load all data at once, this will be set when we export so we get all rows even if it is serverside row model
 */
let loadAllDataFlag = false;

/**
 * Load all data from the server by setting the loadAllData flag and refreshing the cache
 */
const loadAllData = async () => {
    await refreshPromise;
    loadAllDataFlag = true;
    await refreshCachedData();
    loadAllDataFlag = false;
};

/**
 * Datasource for the server side row model
 */
const serversideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
        const columnSort = params.request.sortModel.map((s) => ({
            dataField: gridColumnIdToDataFieldMap.value.get(s.colId)!,
            columnId: s.colId,
            sortDirection: agSortToSortDirection(s.sort)!,
        }));

        // Save the latest columnsort so its available outside the getRows
        lastColumnSort = columnSort;

        // Flatten all the filters
        const filters = Array.from(columnFilters.values()).flat();

        // Set the expandedGroupState
        expandedGroupState.clear();
        params.request.groupKeys?.forEach((g) => expandedGroupState.add(g.toLocaleLowerCase()));

        props.gridSetup.datasource.rowGetter({
            successCallback: (rows: TRowData[], allFilteredRows: TRowData[], lastRow?: number) => {
                lastFilteredRows = allFilteredRows;
                params.success({
                    rowData: rows,
                    rowCount: lastRow,
                });
                if (refreshPromiseResolver) {
                    refreshPromiseResolver();
                }
            },
            startRow: loadAllDataFlag ? 0 : params.request.startRow!,
            endRow: loadAllDataFlag ? 9999999 : params.request.endRow!,
            groupKeys: Array.from(expandedGroupState) ?? [],
            rowGroupCols: params.request.rowGroupCols.map((c) => {
                return {
                    colId: c.id,
                    displayName: c.displayName,
                    dataField: c.field!,
                };
            }),
            columnSort: columnSort,
            sort: (allrows) => defaultSortFunction(allrows, columnSort),
            filter: (allrows) => getFilteredRows(allrows, filters),
            filters: filters,
            parentRow: props.parentrow,
        });
    },
};

/**
 * We redraw the statusbar when data changes
 */
const refreshStatusbar = () => {
    // We need to rerun the datasource rowCountGetter to get the new number of rows
    // Should we do this in the panel instead on a api refresh or something?
    statusBar.value?.statusPanels.forEach((sp) => {
        if (sp.statusPanelParams?.rowCount) {
            sp.statusPanelParams.rowCount();
        }
    });
};

/**
 * If there is any aggregate functions we need to add a row to the pinned bottom row.
 * We use the pinnet bottom row in order to keep the aggregate row visible when we scroll the grid and also it will maintain the column order.
 */
const refreshAggregates = () => {
    if (!gridApi) {
        console.trace('Grid API is not available');
        return;
    }

    // If we have no aggregated functions we dont need to do anything
    if (aggregateFunctions.size == 0) {
        return;
    }

    // We need to check all the columndefs and then add a row to the pinned bottom row with the same fields
    let coldefs = gridApi.getColumnDefs()?.map((c) => c as ColDef) ?? [];

    let row = {} as any; // We need to cast this to any since we want to fill in alle th fields of the datasource, but we are only showing a few of them and always as strings
    coldefs.forEach((c) => {
        if (c.field) {
            row[c.field!] = aggregateFunctions.get(c.field!) ?? null;
        }
    });

    gridApi.setGridOption('pinnedBottomRowData', [row]);

    const pinned = gridApi.getPinnedBottomRow(0);

    if (pinned) {
        gridApi.refreshCells({
            rowNodes: [pinned],
            force: true,
        });
    }
};

/**
 * The default getRowId function.
 * Uses the supplied rowIdGetter if it exists, otherwise it uses the ID property of the row data.
 */
const getRowId = (params: GetRowIdParams<TRowData>) => {
    const groupColumns = params.api.getRowGroupColumns();
    if (groupColumns.length > params.level) {
        const allFields = groupColumns
            .map((c) => c.getColDef().field)
            .filter((f) => f != null)
            .map((f) => (params.data as unknown as any)[f!]);

        if (params.parentKeys) {
            return [...params.parentKeys!, allFields].join('-');
        } else {
            return allFields.join('-');
        }
    }
    // return [...parentKeys, data.athlete, data.year];

    return getRowIdForRow(params.data);
};

/**
 * Get the rowId for a row. based on either the rowIdGetter function or the ID property of the row data.
 * @param row
 */
const getRowIdForRow = (row: TRowData): string => {
    if (props.gridSetup.rowIdGetter) {
        return props.gridSetup.rowIdGetter(row);
    }

    if (!Object.prototype.hasOwnProperty.call(row, 'ID')) {
        throw new Error('Data must have a ID property, or you have to supply a custom rowIdGetter function.');
    }

    return (row as unknown as { ID: any }).ID.toString();
};

const clipboardhandler = useClipboardHandler();

/** When the grid is ready we supply the datasource, and save the gridApi for later use */
const onGridReady = (params: GridReadyEvent) => {
    gridApi = params.api;

    // We always assume we have a ID field and we use that as the default sort.
    // If we dont do this the data will be sorted in the order it is received from the server and that messes up when we update individual lines
    // they will always and up on top of the grid.
    // We should have a prop setting for this since different views wants different default sort ( Bib view want to sort by bib, contestants by name etc. )
    // gridApi!.applyColumnState({
    //     state: [{ colId: 'ID', sort: 'asc' }],
    // });

    gridApi.updateGridOptions({
        serverSideDatasource: serversideDatasource,
        groupRowRenderer: GroupRowRenderer,
        groupRowRendererParams: {},
        loadingCellRenderer: EmptyCellRenderer,
        cellSelection: {
            handle: {
                mode: 'fill',
                direction: 'y',
            },
            suppressMultiRanges: true,
        },

        /**
         * This is a callback that lets us manipulate the data before it is sent to the clipboard.
         * We use this to save the data to the clipboard handler so we can paste it later because we want to different formats.
         * One format that is in the clipboard that is actually the data that is displayed in the cell, and one that is the actual value of the cell.
         * @param params
         */
        processCellForClipboard: (params: ProcessCellForExportParams<TRowData>) => {
            let valueGetterValue = params.value;

            const ourColdef = props.gridSetup.GridColumnDefinition.ColumnOptions.find((c) => c.columnId === params.column.getColId());
            if (ourColdef) {
                // The editorvalue contains the actual value and type from the editor
                const editorValue = ConvertCellRendererValueToString(ourColdef.cellRenderer, params.value, ourColdef.cellRendererParams);

                const item = new ClipboardDataItem(
                    editorValue,
                    valueGetterValue,
                    ourColdef.cellRenderer,
                    ourColdef.columnId,
                    params.node?.id ?? '',
                );

                clipboardhandler.PushToBuffer(item);

                // We always send the text to the clipboard
                valueGetterValue = item.text;
            }

            // This is the value that is sent to the system clipboard and should always be the textual representation that we see in the cell
            // Since this is pr cell. AGGrid will format them as a table with rows separated by \n and columns by \t
            // This is data that can be imported in Excel and other spreadsheet applications
            return valueGetterValue;
        },

        /**
         * This handles the data that is pasted from the clipboard into the grid.
         * It can handle both regular data from external source, and also our own complex data structure
         */
        processDataFromClipboard: (params: ProcessDataFromClipboardParams<TRowData>): string[][] | null => {
            // We need to find the startingpoint for the paste operation
            // We caluclate the rest of the columns based on the pasted data and the visible columns
            let focusedCell = params.api.getFocusedCell();

            // No focused cell means we cant paste
            if (focusedCell == null) {
                return [];
            }

            let clipboardData = clipboardhandler.GetClipboardData(params.data);

            // We need a list of the visible columns so we can map the clipboard data to the correct columns
            const visibleColumns = params.api.getAllDisplayedColumns();

            // We iterate all the visible columns but only start to handle the data when we reach the focused column
            let handledColumnCounter = 0;
            for (const c of visibleColumns) {
                // If we are not at the focused column yet we skip
                if (c.getColId() != focusedCell.column.getColId() && handledColumnCounter == 0) {
                    continue;
                }

                // if we have handled all the columns in the clipboard data we are done
                if (handledColumnCounter >= clipboardData[0].length) {
                    break;
                }

                // We need to find our coldef so we can find the cell editor to convert the data
                const ourColdef = props.gridSetup.GridColumnDefinition.ColumnOptions.find((col) => col.columnId === c.getColId());

                if (ourColdef === undefined || ourColdef.cellEditor === undefined) {
                    console.warn('Could not find coldef or cellEditor for column so we cancel the paste operation', c.getColId());
                    params.data = [];
                    break;
                }

                // Now we iterate all the rows in the clipboard data and set the value in the correct column
                for (let i = 0; i < clipboardData.length; i++) {
                    const rowdata = clipboardData[i];

                    try {
                        let newVal = ConvertClipboardDataToCellEditorValue(
                            ourColdef.cellEditor,
                            rowdata[handledColumnCounter],
                            ourColdef.cellEditorParams,
                        );

                        // Set the new value in the correct column as any to avoid compiler issues
                        clipboardData[i][handledColumnCounter] = newVal as unknown as any;
                    } catch (ex) {
                        console.error(
                            'Error while processing pasted data for column ' + c.getColId() + ': ' + ex,
                            ourColdef.cellEditor,
                            rowdata[handledColumnCounter],
                        );
                        clipboardData = [];
                        break;
                    }
                }

                handledColumnCounter++;
            }

            return clipboardData as unknown as any;
        },
    });

    refreshAggregates();

    refreshStatusbar();

    emit('gridReady', getCurrentGridState());
};

/**
 * This will return the current gridstate. If the grid dont have a gridkey, it does not have a saved state so returns null.
 * @return The current grid state or null if there is no gridkey
 */
const getCurrentGridState = (): CustomGridState | null => {
    if (!props.gridSetup.gridkey) {
        console.warn('Trying to get grid state for a grid without a gridkey');
        return null;
    }

    let gridState: CustomGridState | null = useGridSettings().GetState(props.gridSetup.gridkey!);

    if (gridState != null) {
        return gridState;
    }

    let _state = gridApi.getColumnState();
    const columnState = _state.map((c) => new GridStateColumn(c.colId, c.width ?? undefined, c.flex ?? undefined)) ?? [];
    gridState = new CustomGridState(props.gridSetup.gridkey!);
    gridState.columnOrder = columnState.map((c) => c.colId);
    gridState.columnWidths = columnState;
    gridState.hiddenColumns = _state.filter((x) => x.hide == true).map((x) => x.colId);

    return gridState;
};

/**
 * Reset the grid state to the default state as defined in the props. This will remove any saved state for the grid and reset the column order, visibility and width to the default state.
 */
const resetGridState = () => {
    if (!props.gridSetup.gridkey) {
        console.warn('Trying to reset grid state for a grid without a gridkey');
        return;
    }

    const emptyState = new CustomGridState(props.gridSetup.gridkey!);
    useGridSettings().SaveState(emptyState);

    // We need tou update the state of the columns to apply the new state
    gridApi?.applyColumnState({
        state:
            gridApi.getColumns()?.map((c) => ({
                colId: c.getColId(),
                hide: false,
                width: undefined,
                flex: undefined,
            })) ?? [],
        applyOrder: true,
    });
};

/**
 * Handle the state updated event from AG-Grid. This is called whenever the state is changed in the grid.
 * @param event
 */
const onStateUpdated = (event: StateUpdatedEvent<TRowData>) => {
    // We only want to save the state if there is a change in ColumnOrder, ColumnVisibility or ColumnWidth and we have a gridkey
    if (!props.gridSetup.gridkey || !props.gridSetup.gridkey) {
        return;
    }

    if (
        !event.sources.includes('columnOrder') &&
        !event.sources.includes('columnVisibility') &&
        !event.sources.includes('gridInitializing')
    ) {
        return;
    }

    if (event.sources.includes('gridInitializing')) {
        let gridState: CustomGridState | null = useGridSettings().GetState(props.gridSetup.gridkey!);

        // We need to update the visibility of the columns
        if (gridState?.hiddenColumns && gridState.hiddenColumns.length > 0) {
            const colState = gridState.hiddenColumns.map((colId) => ({
                colId: colId,
                hide: true,
            }));

            event.api?.applyColumnState({
                state: colState,
            });
        }

        emit('onStateUpdated', gridState);
    } else {
        // We get the current state from the gridsettings

        const columnState =
            event.api.getColumnState().map((c) => new GridStateColumn(c.colId, c.width ?? undefined, c.flex ?? undefined)) ?? [];

        // We need to defer this since this get called when we are moving a column across multiple columns, and we only want the last state anyway.
        // Altho there is not a big deal if we save them multiple times
        // Create a State object that we can save
        let state = new CustomGridState(props.gridSetup.gridkey!);
        state.columnOrder = columnState.map((c) => c.colId);
        state.columnWidths = columnState;
        state.hiddenColumns = event.state.columnVisibility?.hiddenColIds ?? [];

        statesaveDebounce(() => {
            useGridSettings().SaveState(state);
        }, 3000);

        emit('onStateUpdated', state);
    }
};

// We start the dirty cell process when we start editing a cell
const dirtyCellStart = (node: IRowNode<TRowData>, column: Column, value: any) => {
    // Create the dirty object for the row if it doesnt exist
    if (!dirtyCells[node.id!]) {
        dirtyCells[node.id!] = {};
    }

    // Set the original value if we dont have it
    if (dirtyCells[node.id!][column!.getId()] === undefined) {
        dirtyCells[node.id!][column!.getId()] = JSON.stringify(value);
    }
};

const dirtyCellStop = (node: IRowNode<TRowData>, column: Column, newvalue: any, oldvalue?: any) => {
    // Check if the new value is the same as the original and remove the dirty
    if (dirtyCells[node.id!] && dirtyCells[node.id!][column!.getId()]) {
        let newValue = JSON.stringify(newvalue);

        if (oldvalue === undefined) {
            oldvalue = dirtyCells[node.id!][column!.getId()];
        } else {
            oldvalue = JSON.stringify(oldvalue);
        }

        if (dirtyCells[node.id!][column!.getId()] == newValue) {
            delete dirtyCells[node.id!][column!.getId()];
            // if the dirty object is empty we remove it
            if (Object.keys(dirtyCells[node.id!]).length === 0) {
                delete dirtyCells[node.id!];
            }
        }

        if (oldvalue != newValue) {
            emit('cellValueChanged', node.data!, column!.getId(), newvalue, oldvalue);
        }
    }
};

/**
 * Triggered when a cell is edited and the user exits the cell.
 * @param event
 */
const onCellEditingStopped = (event: CellEditingStoppedEvent<TRowData> | CellKeyDownEvent<TRowData>) => {
    if (!editHasStarted) {
        return;
    }

    dirtyCellStop(event.node, event.column!, event.value);

    editHasStarted = false;

    // Reaggregate the data
    refreshAggregates();

    emit('onCellEditingStopped', event.node.data!, event.column!.getId(), event.node.id);
};

/**
 * Emits the id of the row that is selected
 * @param event
 */
const onRowSelected = (event: RowNodeEvent<'rowSelected'>) => {
    emit('onRowSelected', event.node.id!);
};

/**
 * If the grid is used as a detail grid we need to check if the gridDetailView is set
 */
const isMasterDetailGrid = () => props.gridDetailView !== undefined || props.gridSetup.detailGridSetup !== undefined;

/**
 * Emits the keydown event on the cell and handle the delete key
 * @param event
 */
const onCellKeyDown = (event: CellKeyDownEvent<TRowData>) => {
    // TODO: Maybe change this for a more generic approach either via rowIdGetter, or a prop setting
    if (event.colDef?.field === 'ID' && (event.event as KeyboardEvent).key === 'Enter') {
        emit('onIdColumnClicked', event?.value);
    }
    emit('onCellKeyDown', event.event as KeyboardEvent);
    // If we press Delete on a cell that is not in edit mode we want to emit the event onCellEditingStopped so we can
    // handle it and make sure we update values that we pick up on the onCellEditingStoppen event
    if (!(event.event?.srcElement instanceof HTMLInputElement) && (event.event as KeyboardEvent).key === 'Delete') {
        emit('onCellEditingStopped', event.node.data!, event.column!.getId());
    }
};

const onSortChanged = (event: SortChangedEvent<TRowData>) => {
    emit('sortChanged');
};

const onCellDoubleClicked = (event: CellDoubleClickedEvent<TRowData>) => {
    const row = event.data;

    if (!row) {
        return;
    }

    emit('onCellDoubleClicked', row, event.value, event.node.id!, event.column.getId());
};

/**
 * We need to emit the rowNode and columnid for the cell that is focused.
 * @param event
 */
const onCellFocused = (event: CellFocusedEvent<TRowData>) => {
    if (event.rowIndex == null) return; // You will be tempted to write this as if(!event.rowIndex) return; but that will not work as !0 is true. Undefined is handled too because undefined == null is true

    const rowNode = gridApi.getDisplayedRowAtIndex(event.rowIndex) as IRowNode<TRowData>;
    const column = event.column as Column<TRowData>;

    emit('onCellFocused', rowNode.id!, column.getId());
};

/**
 * We need to emit the rowNode and columnid for the cell that is focused.
 * @param event
 */
const onCellClicked = (event: CellClickedEvent<TRowData>) => {
    if (event.rowIndex == null) return; // You will be tempted to write this as if(!event.rowIndex) return; but that will not work as !0 is true. Undefined is handled too because undefined == null is true

    const rowNode = gridApi.getDisplayedRowAtIndex(event.rowIndex) as IRowNode<TRowData>;
    const column = event.column as Column<TRowData>;

    // If the input is of type bool/checkbox we trigger the editor that will pick up the click and handle the toggling without opening the editor
    if (['InputRadio', 'InputBool'].includes(event.colDef.cellEditorParams?.comp?.__name)) {
        gridApi.startEditingCell({ rowIndex: event.rowIndex, colKey: column.getId() });
    }

    emit('onCellClicked', rowNode.id!, column.getId());
};

/**
 * We handle all the drag and fill functionality ourselves here.
 * We do this so we can set the new values with the correct types, and also control intervals better
 * We want numeric intervals to be 1, and date intervals to be a variable, but default to 30 seconds
 * We get intervals by holding the ALT key while dragging

 * NOTE: This will also trigger the CellValueChanged event so we dont need to do that here and that handles the dirty cells and validation
 */
const onFillEnd = (event: FillEndEvent<TRowData>) => {
    let sourceRowStartIndex = event.initialRange.startRow?.rowIndex ?? 0;
    let sourceRowEndIndex = event.initialRange.endRow?.rowIndex ?? 0;
    let sourceValueMap: ISourceValue[][] = [];
    let gotDateType = false;
    let gotTimespanType = false;

    // We try and figure out what data we are dealing with in the source range
    for (let i = sourceRowStartIndex; i <= sourceRowEndIndex; i++) {
        let values: ISourceValue[] = [];
        for (let sourceColumn of event.initialRange.columns) {
            let colDef = sourceColumn.getColDef();
            let rowNode = gridApi.getDisplayedRowAtIndex(i);
            let sourceValue = null;

            if (colDef.valueGetter instanceof Function) {
                sourceValue = (colDef.valueGetter as Function)(rowNode);
            }

            const guessedType = guessValueType(sourceValue);

            if (guessedType.guess == 'Date') {
                gotDateType = true;
            }

            if (guessedType.guess == 'TimeSpan') {
                gotTimespanType = true;
            }

            values.push({ value: sourceValue, type: guessedType.guess, actualType: guessedType.actual });
        }
        sourceValueMap.push(values);
    }

    if (!gotDateType && !gotTimespanType) {
        // If we have no date types we can just return here and let ag-grid handle the rest
        // For now we only need to handle the date types since they are a bit special and aggrid does not handle them well
        return;
    }

    let destinationRowsStartIndex = sourceRowEndIndex + 1;
    let destinationRowEndIndex = event.finalRange.endRow?.rowIndex ?? 0;
    let direction: number = sourceRowStartIndex > (event.finalRange.startRow?.rowIndex ?? 0) ? -1 : 1;

    // We need to loop over the destination rows and columns and set the values based on the sourceValueMap
    if (direction == -1) {
        // The destinationRowsSTartIndex and destinationRowEndIndex are different when we drag up so we switch them
        destinationRowEndIndex = sourceRowStartIndex - 1;
        destinationRowsStartIndex = event.finalRange.startRow?.rowIndex ?? 0;
    }

    let currentSourceIndex = direction == 1 ? -1 : destinationRowEndIndex - destinationRowsStartIndex + 1;

    // We just manipulate the rowCounter so we dont need two loops for different directions
    let rowCounter = direction == 1 ? 1 : destinationRowEndIndex - destinationRowsStartIndex + 1;
    let nodes: IRowNode<any>[] = [];
    let destinationRowCounter = 0;
    for (let i = destinationRowsStartIndex; i <= destinationRowEndIndex; i++) {
        let rowNode = gridApi.getDisplayedRowAtIndex(i);
        if (!rowNode) {
            continue;
        }
        destinationRowCounter++;

        currentSourceIndex = (currentSourceIndex + 1 * direction) % sourceValueMap.length;
        let values = sourceValueMap[currentSourceIndex] as ISourceValue[];

        for (let j = 0; j < values.length; j++) {
            // Here we handle each column.

            let currentValue = values[j].value;
            const currentType = values[j].type;

            // We only handle Date columns since the integer columns is poperly handled by ag-grid itself
            if (currentType != 'Date' && currentType != 'TimeSpan') {
                continue;
            }

            if (currentType == 'Date') {
                // We find the value of the firs and last source values to calculate the actual interval
                const firstValue: Date = sourceValueMap[0][j].value as Date;
                const lastValue: Date = sourceValueMap[sourceValueMap.length - 1][j].value as Date;

                // The actual interval is then milliseconds for date and number for numeric
                const actualInterval: number = lastValue.getTime() - firstValue.getTime();

                // Make intervals
                if (actualInterval > 0) {
                    currentValue = new Date(lastValue.getTime() + actualInterval * destinationRowCounter * direction);
                }
            } else if (currentType == 'TimeSpan') {
                const firstValue: TimeSpan = sourceValueMap[0][j].value as TimeSpan;
                const lastValue: TimeSpan = sourceValueMap[sourceValueMap.length - 1][j].value as TimeSpan;

                // The actual interval is then milliseconds for date and number for numeric
                const actualInterval: number = lastValue.TotalMilliseconds - firstValue.TotalMilliseconds;

                // Make intervals
                if (actualInterval > 0) {
                    currentValue = TimeSpan.FromMilliseconds(
                        lastValue.TotalMilliseconds + actualInterval * destinationRowCounter * direction,
                    );
                }
            }

            rowNode.setDataValue(event.finalRange.columns[j], currentValue);
        }

        // We need to know what nodes to refresh
        nodes.push(rowNode);

        rowCounter = rowCounter + direction;
    }

    let colids = event.finalRange.columns.map((c) => c.getColId());

    // refersh the grid
    gridApi.refreshCells({ columns: colids, rowNodes: nodes, force: true });
};

/**
 * this is triggered whenever a cell value is changed. It is also triggered when we copy and paste values in the grid
 * @param event
 */
const onCellValueChanged = (event: CellValueChangedEvent<TRowData>) => {
    // NOTE! This one doesnt get called anymore. I suspect it is becuase somewhere in a wrapper cell we set the value directly on the
    // rowdata and dont use the api.setDataValue.

    // TODO: There might be an issue with newvalue vs oldvalue here since we dont know the type of the data so copy an integer will make it into a string
    dirtyCellStart(event.node, event.column, event.oldValue);
    dirtyCellStop(event.node, event.column, event.newValue, event.oldValue);

    // Should we debounce this and put the changes in a list and try and process them at all if we paste many values at once?
    // Dont know if thet actually matters much in case of processing speed
    gridApi.refreshCells({ columns: [event.column.getId()], rowNodes: [event.node], force: true });
};

// We nned this to keep track that an edit has started so we can ignore the cellEditingStopped event if it wasnt started by us
let editHasStarted = false;

/**
 * This is triggered when we start editing a cell
 * @param event
 */
const onCellEditingStarted = (event: CellEditingStartedEvent<TRowData>) => {
    // We ignore this event if it was started by TAB, that is because it was triggered when we leave the edit of the previous cell
    if (event.event instanceof KeyboardEvent && event.event.key === 'Tab') {
        return;
    }

    const rowNode = event.node;

    if (isAggregateColumn(event.colDef.field!) && rowNode.rowPinned == 'bottom') {
        gridApi.stopEditing();
        return;
    }

    const ourColdef = props.gridSetup.GridColumnDefinition.ColumnOptions.find((c) => c.columnId === event.column!.getId());

    // This will pass the keydown event to the wrapper so we can handle it in each editor
    let editor = gridApi.getCellEditorInstances()[0] as any; // AgCelleditorWrapper we could make a interface for this

    editor?.cellEditingStarted(event.event, ourColdef);

    editHasStarted = true;

    // We need to keep track of the original value so we can check if the cell is dirty
    dirtyCellStart(event.node, event.column, event.value);

    // If the input is of type bool/checkbox we dont need to open a editor, we can just toggle the value
    if (['InputRadio', 'InputBool'].includes(event.colDef.cellEditorParams?.comp?.__name)) {
        rowNode.setDataValue(event.column!.getId(), !event.value);
        gridApi.stopEditing(true);
    }

    emit('onCellEditingStarted', rowNode.data!, event.column!.getId(), rowNode.id);

    gridApi.refreshCells({
        columns: [event.column!.getId()],
        rowNodes: [event.node!],
        force: true,
    });
};

/**
 * This is triggered when the grid is rendered for the first time. Normally we would resize the columns here but since we
 * almost always load the data after the grid is rendered the autoresize will be based on headers only
 * @param event
 */
const onFirstDataRendered = (event: FirstDataRenderedEvent<TRowData>) => {
    if (!props.disableGridAutoResizeColumnsOnLoad) {
        resizeColumns();
    }
    emit('onFirstDataRendered');
};

/**
 * This is triggered when the grid is resized. We need to resize the columns here so they fit the new size of the grid
 * If we have a saved state we will use that to resize the columns unless we force it
 * The autoresize will defer 100 ms so we will wait until we have the data in the grid before we resize the columns
 */
const resizeColumns = (force?: boolean) => {
    if (!gridApi) {
        return;
    }

    // If we have a gridkey we try to load the state from the settings. If we dont find a state we will run the general resize columns function
    if (force !== true && props.gridSetup.gridkey) {
        let gridState: CustomGridState | null = useGridSettings().GetState(props.gridSetup.gridkey!);

        // Only run resize columns if we dont have a preset gridState ( Should almost never happen )
        if (gridState !== null) {
            let colwidths = gridState.columnWidths?.map((c) => {
                return {
                    colId: c.colId,
                    width: c.width,
                    flex: c.flex,
                } as ColumnSizeState;
            });

            // Sort colwidths based on the order of the gridState.columnOrder
            colwidths = colwidths?.sort((a, b) => {
                const aIndex = gridState.columnOrder?.indexOf(a.colId);
                const bIndex = gridState.columnOrder?.indexOf(b.colId);
                return aIndex! - bIndex!;
            });

            gridApi?.applyColumnState({ state: colwidths, applyOrder: true });

            return;
        }
    }

    // TODO: Her har vi 2 forskjellige måter å size columns på.
    // Vi må enten legge inn en setting så vi kan styre det, eller så må vi velge en
    // Trenger vel uansett en default, så vi får ta valg i felleskap på ett eller anna tidspunkt.
    if (props.resizeStrategy == 'sizeColumnsToFit') {
        resizeDebounce(() => gridApi.sizeColumnsToFit(), 100);
    }

    // This one need to be delayed or else we only resize on the values of the columns. I think this is probably caused by the fact that we always fill the grid with data later
    // We should have a event when the first time we have data in the grid so we can resize then
    // We defer this since we dont want to resize the columns multiple times if we are resizing the grid
    if (props.resizeStrategy == 'fitToContent') {
        // Find the columns to resize
        const resizeColumnIds =
            props.resizeColumnIds ??
            gridApi
                .getColumns()
                ?.filter((c) => (force ? true : c.getColDef().minWidth === undefined))
                .map((c) => c.getColId())
                .filter((id) => id !== 'ID') ??
            []; // Fallback to empty array if we dont have any columns. This should NEVER happen

        resizeDebounce(() => {
            gridApi.autoSizeColumns(resizeColumnIds);
        }, 100);
    }
};

/**
 * Get the selected row state from the gridApi.
 */
const getSelectedRowState = () => rowSelectionState;

/**
 * Return the selected rows based on the current selection state.
 * @param data
 */
const getSelectedRows = (): TRowData[] => {
    const state = getSelectedRowState();

    if (lastFilteredRows == null) {
        console.warn(
            'No rows available to select from. This might be because the grid is not ready yet or the data has not been loaded. This should never happen.',
        );
        return [];
    }

    if (state.selectAll) {
        if (state.toggledNodes.length > 0) {
            return lastFilteredRows.filter((row) => !state.toggledNodes.includes(getRowIdForRow(row)));
        }
        return lastFilteredRows;
    } else {
        if (state.toggledNodes.length > 0) {
            return lastFilteredRows.filter((row) => state.toggledNodes.includes(getRowIdForRow(row)));
        }
        return [];
    }
};

// The internal state for the selection
let rowSelectionState: GridSelectionState = new GridSelectionState({ selectAll: false, toggledNodes: [] });

/**
 * This is triggered when the selection changes in the grid. We emit the selected rows
 * @param event
 */
const onSelectionChanged = (event: SelectionChangedEvent<TRowData>) => {
    if (!event.serverSideState || !('selectAll' in event.serverSideState)) {
        // If the state does not have selectAll, we assume it is a new version of AG Grid that does not support selectAll
        // We return a new GridSelectionState with selectAll set to false and toggledNodes set to the current selection
        return;
    }

    rowSelectionState = new GridSelectionState(event.serverSideState);

    emit('onSelectionChanged', getSelectedRows());
};

/**
 * This is triggered when the mouse is over a cell. We emit the row data
 * @param event
 */
const onCellMouseOver = (event: CellMouseOverEvent<TRowData>) => {
    // We dont do anything if we dont have the row data.
    // This will only be missing if we are hovering over a group. We havent used groups yet
    if (!event.data) {
        return;
    }
    emit('onCellMouseOver', event.data);
    emit('rowHovered', event.data);
};

/**
 * This is triggered when the mouse leaves a cell. We emit the row data
 * @param event
 */
const onCellMouseOut = (event: CellMouseOutEvent<TRowData>) => {
    // We dont do anything if we dont have the row data.
    // This will only be missing if we are hovering over a group. We havent used groups yet
    if (!event.data) {
        return;
    }
    emit('onCellMouseOut', event.data);
    emit('rowHovered', undefined);
};

/**
 * Transforms our {@link IGridColumnCssRule} to AG Grid {@link CellClassParams} to be consumed by AG Grid
 * This will also add a global rule for dirty cells
 * @param cellClassRules The array of {@link IGridColumnCssRule} rules for the column we are handling
 */
const buildAgCellClassRules = (cellClassRules?: Array<IGridColumnCssRule<TRowData, any>>) => {
    // Map CellRules to AG Grid
    let agCellClassRules = {} as Record<string, (params: CellClassParams<TRowData, any>) => boolean>; // TODO: The any on this line is actually TCellData. This should be fixed at some point

    cellClassRules?.forEach((rule) => {
        agCellClassRules![rule.className] = (cell: CellClassParams<TRowData, any>) => {
            // Note: This is here because AG Grid will try to render cells before the data exists.
            // If the row doesnt exist, do nothing
            if (cell.data === undefined) {
                return false;
            }

            // Note: If the row exists we also just assume the cell has its colId and dataField set as well
            // Note2: the AgGrid .editable can also contain a function that returns a boolean, we dont use this so for now we assume is always a type

            return rule.func(cell.value, cell.data, cell.colDef.colId!, cell.colDef.field!, (cell.colDef.editable as boolean) ?? true);
        };
    });

    // We add a global rule for dirty cells
    agCellClassRules['ag-cell-dirty'] = (cell: CellClassParams<TRowData, any>) => {
        if (!gridApi || Object.keys(dirtyCells).length === 0) return false;

        if (dirtyCells[cell.node.id!] !== undefined && dirtyCells[cell.node.id!][cell.colDef.field!] !== undefined) {
            return dirtyCells[cell.node.id!][cell.colDef.field!] != JSON.stringify(cell.value);
        }

        return false;
    };

    return agCellClassRules;
};

/**
 * Transforms our {@link GridColumnOptions.rowClassRules} to AG Grid {@link RowClassParams} to be consumed by AG Grid
 * @param columndef The column definition for the column we are handling
 */
const agRowClassRules = computed(() => {
    if (!props.gridSetup.rowClassRules) {
        return undefined;
    }
    // Map CellRules to AG Grid
    let agRowClassRules = {} as Record<string, (params: RowClassParams<TRowData>) => boolean>;

    props.gridSetup.rowClassRules.forEach((rule) => {
        agRowClassRules![rule.className] = (params: RowClassParams<TRowData>) => {
            if (!params.data) {
                return false;
            }

            return rule.func(params.data, params.rowIndex!);
        };
    });

    return agRowClassRules;
});

/**
 * We find the property that this should be binded to and return the correct type
 * @param data
 */
const getVmodelValueAsCorrectType = (data: any, editor?: any) => {
    const datatype = guessValueType(data).guess;

    if (datatype == 'Text') {
        return data;
    } else if (datatype == 'Numeric') {
        // Check if the key is a number or a dot using regex
        if (/^-?[0-9.]+$/.test(data)) {
            return parseFloat(data);
        } else {
            return null;
        }
    } else {
        return data;
    }
};

// We need to keep a list of the aggregate functions for each column so we can calculate the totals
const aggregateFunctions = new Map<string, IGridAggregate>();

const isAggregateColumn = (fieldId: string) => aggregateFunctions.has(fieldId);

const gridSelectionColumnDef = computed<SelectionColumnDef | undefined>(() => {
    if (!props.gridSetup.hasCheckboxSelectionColumn) {
        return undefined;
    }

    if (!props.gridSetup.GridColumnDefinition.CheckboxSelectionColumn) {
        return {
            width: 30,
            maxWidth: 30,
            minWidth: 30,
            resizable: false,
            sortable: false,
            pinned: 'left',
        };
    }

    return checkboxSelectionColumnDefToAgGridColdef(props.gridSetup.GridColumnDefinition.CheckboxSelectionColumn);
});

const detailCellRendererParams = computed(() => {
    return {
        detailgridsetup: props.gridSetup.detailGridSetup,
        detailview: props.gridDetailView,
        registerGrid: registerGrid,
        detailtitle: props.gridDetailTitle,
    };
});

const autoGroupColumnDef = {
    minWidth: 100,
    maxWidth: 100,
};

const gridOptions = computed<GridOptions<TRowData>>(() => {
    return {
        rowHeight: 22,
        rowModelType: 'serverSide',
        rowSelection: !props.gridSetup.hasCheckboxSelectionColumn
            ? undefined
            : {
                  checkboxes: props.gridSetup.hasCheckboxSelectionColumn,
                  checkboxLocation: 'selectionColumn',
                  mode: props.gridSetup.rowSelection == 'single' ? 'singleRow' : 'multiRow',
              },
        pinnedTopRowData: props.gridSetup.datasource.pinnedTopRowData,
        suppressColumnVirtualisation: props.keepColumnsOutsideViewport,
        cacheBlockSize: props.gridSetup.blockCacheSize,
    };
});

const checkboxSelectionColumnDefToAgGridColdef = (columndef: GridCheckboxSelectionColumnOptions<TRowData>): SelectionColumnDef => {
    return {
        resizable: columndef.resizable ?? true,
        pinned: columndef.pinned,
        suppressMovable: columndef.suppressMovable ?? false,
        rowDrag: columndef.draggable instanceof Function ? (p) => (columndef.draggable as Function)(p.data) : columndef.draggable,
        // TODO: How should we define where to put the expand button? Should we have a prop for this? Now I just use the ID column
        cellRenderer: AgCellRendererWrapper<TRowData, any>,
        cellRendererParams: {
            grid: exposed,
            comp: columndef.cellRenderer,
            componentProps: columndef.cellRendererParams,
            valueGetter: columndef.valueGetter ?? (() => null),
            cellRefreshTrigger: columndef.cellRefreshTrigger,
            isCheckboxColumn: true,
        },
        // This exists in the SelectionColumnDef, but its not implemented for the CheckboxSelectionColumn yet
        // cellRendererSelector: (params: ICellRendererParams<TRowData, any>) => {
        //     if (params.node.rowPinned == 'bottom') {
        //         return { component: AggregateCellRenderer };
        //     }
        //     return undefined;
        // },

        width: columndef.width,
        maxWidth: columndef.maxWidth,
        minWidth: columndef.minWidth,
        flex: columndef.flex,
        cellClassRules: buildAgCellClassRules(columndef.cellClassRules),
    };
};

/**
 * The column definitions for the grid as AgGrid types
 */
const columnDefs = computed(() => {
    return props.gridSetup.GridColumnDefinition.ColumnOptions.map((co, i) => {
        // merge with common options
        const nco = new GridColumnOptions<TRowData>(co);

        for (const key in props.gridSetup.GridColumnDefinition.CommonColumnOptions) {
            const kk = key as keyof GridColumnOptions<TRowData>;
            if (kk === propertyof<GridColumnOptions<TRowData>>('columnId') || kk == 'columnId') {
                continue;
            }
            if (nco[kk] === undefined) {
                nco[kk] = props.gridSetup.GridColumnDefinition.CommonColumnOptions[kk];
            }
        }

        // Find the rowRefreshTrigger. We only want this on the very first row
        const rowRefreshTrigger = i == 0 ? props.gridSetup.rowRefreshTrigger : undefined;

        return columnDefToAgGridColdef(nco, rowRefreshTrigger);
    });
});

const columnDefToAgGridColdef = (
    columndef: GridColumnOptions<TRowData>,
    rowRefreshTrigger?: (row?: TRowData) => any,
): ColDef<TRowData, any> => {
    if (columndef.aggregate) {
        aggregateFunctions.set(columndef.dataField!, columndef.aggregate);
    }

    /**
     * Value getter for the column
     * @param rowdata The row data
     * @param field The field to get the value from
     */
    let valueGetter = (rowdata: TRowData, field: string) => {
        if (field === undefined) {
            throw new Error('Can not have a grid column without a field');
        }

        if (columndef.valueGetter instanceof Function) {
            return (columndef.valueGetter as Function)(rowdata);
        }

        const { data, field: lastField } = getNestedObjectAndField(rowdata, field);

        // if (!Object.hasOwn(data, lastField)) {
        if (!(lastField in data)) {
            throw new Error(`Can not find field in data object. Field ${field} does not exist in ${rowdata}. Failed at ${lastField}.`);
        }

        return data[lastField];
    };

    const editable = !columndef.cellEditor
        ? false
        : columndef.editable instanceof Function
          ? (p: EditableCallbackParams<TRowData, any>) => (columndef.editable as Function)(p.data)
          : (columndef.editable ?? false);

    /**
     * Value setter for the column
     * @param rowdata The row data
     * @param field The field to set the value on
     * @param newvalue The new value to set
     */
    let valueSetter = (rowdata: TRowData, field: string, newvalue: any) => {
        // Run custom setter if defined
        if (columndef.valueSetter instanceof Function) {
            (columndef.valueSetter as Function)(rowdata, newvalue);

            return newvalue;
        }

        const { data, field: lastField } = getNestedObjectAndField(rowdata, field);

        if (!(lastField in data)) {
            throw new Error(`Can not find field in data object. Field ${field} does not exist in ${rowdata}. Failed at ${lastField}.`);
        }

        // TODO: Here we need to handle the correct type. We should probably have a type on the column definition that we can use to cast the value
        // For now we try and get it from the vModel type
        // The reason we need to handle this is because the DragAndFill events or the keyboard events will always have the value as a string. But the vModels of the
        // editors does not all support string.
        // We know the that property we have to bind agains is the DetailEditors modelValue prop. So we check the type of that prop and cast the value to that type
        data[lastField] = getVmodelValueAsCorrectType(newvalue, columndef.cellEditor);

        return newvalue;
    };

    return {
        colId: columndef.colId,
        resizable: columndef.resizable ?? true,
        sortable: columndef.sortable,
        sort: sortDirectionToAgSort(columndef.sortdirection),
        filter: columndef.filterable,
        editable: editable,
        rowGroup: columndef.group,
        suppressFillHandle: columndef.suppressFillHandle || !editable, // This wont work in the cases where editable is a function.
        hide: columndef.hidden,
        pinned: columndef.pinned,
        suppressMovable: columndef.suppressMovable ?? false,
        rowDrag: columndef.draggable instanceof Function ? (p) => (columndef.draggable as Function)(p.data) : columndef.draggable,
        field: columndef.dataField as ColDefField<TRowData, any>,
        // TODO: How should we define where to put the expand button? Should we have a prop for this? Now I just use the ID column
        cellRenderer: AgCellRendererWrapper<TRowData, any>,
        cellRendererParams: {
            grid: exposed,
            comp: markRaw(columndef.cellRenderer),
            componentProps: columndef.cellRendererParams,
            valueGetter: valueGetter,
            cellRefreshTrigger: columndef.cellRefreshTrigger,
            rowRefreshTrigger: rowRefreshTrigger,
        },
        cellRendererSelector: (params: ICellRendererParams<TRowData, any>) => {
            if (params.node.rowPinned == 'bottom') {
                return { component: AggregateCellRenderer };
            }
            return undefined;
        },
        cellEditor: AgCellEditorWrapper,
        cellEditorParams: {
            grid: exposed,
            comp: columndef.cellEditor != undefined ? markRaw(columndef.cellEditor) : undefined,
            componentProps: columndef.cellEditorParams,
            valueGetter: valueGetter,
            valueSetter: valueSetter,
            cellRefreshTrigger: columndef.cellRefreshTrigger,
        },
        cellEditorSelector: (params: ICellEditorParams<TRowData, any>) => {
            if (params.node.rowPinned == 'bottom') {
                return { component: AggregateCellRenderer };
            }
            return undefined;
        },

        loadingCellRenderer: EmptyCellRenderer,

        // headerName: columndef.columnHeaderText,
        headerValueGetter:
            columndef.headerValueGetter instanceof Function
                ? () => columndef.headerValueGetter!(columndef.columnHeaderText ?? '')
                : () => $tm(columndef.columnHeaderText!, '', false),
        width: columndef.width,
        maxWidth: columndef.maxWidth,
        minWidth: columndef.minWidth,
        flex: columndef.flex,
        cellClass: columndef.cellClass,
        cellClassRules: buildAgCellClassRules(columndef.cellClassRules),
        context: columndef.contextData, // TODO: Maybe this should be our columnDef instead?
        headerComponent: AgHeaderComponentWrapper<TRowData, any>,
        headerComponentParams: {
            grid: exposed,
            comp: columndef.headerComponent != undefined ? markRaw(columndef.headerComponent) : StandardHeader,
            componentProps: columndef.headerComponentParams,
            col: { colId: columndef.columnId, dataField: columndef.dataField, filterable: columndef.filterable },
        },

        valueGetter: (params: ValueGetterParams<TRowData, any>) => {
            if (params.data === undefined) return undefined;

            return valueGetter(params.data!, columndef.dataField);
        },

        // valueParser: c.valueParser,
        valueSetter: (params: ValueSetterParams<TRowData, any>) => {
            if (params.data === undefined) return false;

            const field = params.colDef?.field;

            if (field === undefined) {
                throw new Error('Can not have a grid column without a field');
            }

            valueSetter(params.data!, field, params.newValue);
            return true;
        },
        valueParser: (params: ValueParserParams<TRowData, any>) => {
            return params.newValue;
        },
    };
};

/**
 * Finds the last object in a nested object and the last part of the field string that points into that object
 * ```
 * const data = { a: b: { c: 1 } };
 * getNestedObjectAndField(data, 'a.b.c'); // { data: { c: 1 }, field: 'c' }
 * ```
 * @param data The object with potentially nested children
 * @param field The field we are looking for in the form of 'parent.child.child'. This will get reduced as we recurse into the object.
 */
const getNestedObjectAndField = (data: any, field: string) => {
    if (!field.includes('.')) {
        return { data, field };
    }

    const fields = field.split('.');
    let value = data;

    for (const field of fields.slice(0, -1)) {
        // If we fail to find the next level in the object, fallback to the fallback value.
        // if (!Object.hasOwn(value, field)) {
        if (!(field in value)) {
            throw new Error(`Can not find field in data object. Field ${field} does not exist in ${data}. Failed at ${field}.`);
        }
        value = value[field];
    }

    return { data: value, field: fields[fields.length - 1] };
};

onMounted(() => {
    // We add the event listener directly on the grid element so we can catch the alt key
    // gridElement.value?.addEventListener('keydown', (e) => (e.altKey ? (altkeyActive = true) : null));
    // gridElement.value?.addEventListener('keyup', (e) => (!e.altKey ? (altkeyActive = false) : null));
});

onUnmounted(() => {
    // gridElement.value?.removeEventListener('keydown', (e) => (e.altKey ? (altkeyActive = true) : null));
    // gridElement.value?.removeEventListener('keyup', (e) => (!e.altKey ? (altkeyActive = false) : null));
});

/**
 * Expose the grid functions
 */
defineExpose<IGridCompExposed<TRowData>>(exposed);
</script>
